import React, { useCallback, useEffect, useState } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	InputAdornment,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	MenuItem,
	TextField,
	Typography,
} from '@mui/material';

import {
	DEAL_TYPES,
	ICommissionRate,
	IPartner,
	listCommissionRates,
	searchPartners,
} from '../../../Services/partners';

// Association d'un partenaire à un BA, en DEUX temps : on choisit d'abord le partenaire,
// puis on pose les TERMES DE L'AFFAIRE (type, taux, début de commissionnement).
//
// ⚠️ Cette seconde étape est la raison d'être de cet écran. L'association envoyait
// seulement `{partnerId, billingAccountId}` : l'affaire naissait donc avec les valeurs par
// défaut de l'API, et le taux réellement appliqué venait d'ailleurs — du programme et du
// niveau du PARTENAIRE. Ces deux notions ont disparu, et c'est maintenant
// `attribution.rate` qui est appliqué à chaque règlement du client. Sans cette étape,
// l'admin n'aurait aucun endroit où fixer ce que touche le partenaire.
//
// Le taux se pré-remplit depuis la grille au changement de type, et reste MODIFIABLE :
// la grille donne le tarif standard, l'affaire peut être négociée.

type Props = {
	open: boolean;
	onClose: () => void;
	onConfirm: (partner: IPartner, commissionType: string, rate: number, startAt: string) => void;
};

// #region todayInput — date du jour au format d'un <input type="date">
const todayInput = () => new Date().toISOString().slice(0, 10);
// #endregion

// #region useDealRates — taux par défaut, par type d'affaire
const useDealRates = (open: boolean) => {
	const [rates, setRates] = useState<Record<string, number>>({});
	useEffect(() => {
		if (!open) return;
		listCommissionRates()
			.then((rows: ICommissionRate[]) => {
				const byType: Record<string, number> = {};
				(rows || []).forEach((row) => {
					byType[row.commissionType] = row.rate;
				});
				setRates(byType);
			})
			.catch(() => setRates({}));
	}, [open]);
	return rates;
};
// #endregion

// #region PartnerSearchStep
const PartnerSearchStep: React.FC<{ onPick: (p: IPartner) => void }> = ({ onPick }) => {
	const [query, setQuery] = useState('');
	const [results, setResults] = useState<IPartner[]>([]);
	const [searching, setSearching] = useState(false);

	const doSearch = () => {
		if (!query.trim()) return;
		setSearching(true);
		searchPartners(query.trim())
			.then((rows) => setResults(rows || []))
			.catch(() => setResults([]))
			.finally(() => setSearching(false));
	};

	return (
		<>
			<Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
				<TextField
					autoFocus
					fullWidth
					size="small"
					label="Rechercher un partenaire (nom)"
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === 'Enter') doSearch();
					}}
				/>
				<Button variant="contained" onClick={doSearch} disabled={searching}>
					Rechercher
				</Button>
			</Box>

			{searching ? (
				<Box sx={{ py: 3, textAlign: 'center' }}>
					<CircularProgress size={24} />
				</Box>
			) : (
				<List sx={{ mt: 1 }}>
					{results.length === 0 ? (
						<Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
							Aucun résultat — lance une recherche.
						</Typography>
					) : (
						results.map((partner) => (
							<React.Fragment key={partner.id}>
								<ListItem disablePadding>
									<ListItemButton onClick={() => onPick(partner)}>
										<ListItemText
											primary={partner.name}
											secondary={[partner.city, partner.country].filter(Boolean).join(' · ')}
										/>
									</ListItemButton>
								</ListItem>
								<Divider component="li" />
							</React.Fragment>
						))
					)}
				</List>
			)}
		</>
	);
};
// #endregion

// #region DealTermsStep
const DealTermsStep: React.FC<{
	partner: IPartner;
	rates: Record<string, number>;
	onBack: () => void;
	onConfirm: (commissionType: string, rate: number, startAt: string) => void;
}> = ({ partner, rates, onBack, onConfirm }) => {
	// Défaut `leads` : une association POSÉE À LA MAIN est un apport d'affaire. Les
	// affaires `partner` naissent du lien d'affiliation ou du lien de paiement reseller,
	// jamais d'ici — mais le choix reste ouvert, un cas de rattrapage arrivera.
	const [commissionType, setCommissionType] = useState('leads');
	const [rate, setRate] = useState<string>('');
	const [touched, setTouched] = useState(false);
	// Défaut aujourd'hui = comportement d'avant. Antidater sert au contrat signé avant
	// l'association : sans ça, ses factures encaissées entre-temps ne commissionnent pas.
	const [startAt, setStartAt] = useState(todayInput);

	// Tant que l'admin n'a pas saisi de taux, il suit le type choisi. Dès qu'il en pose
	// un, on ne l'écrase plus : sinon changer d'avis sur le type effacerait en silence un
	// taux négocié déjà tapé.
	useEffect(() => {
		if (touched) return;
		const fromGrid = rates[commissionType];
		setRate(fromGrid === undefined ? '' : String(fromGrid));
	}, [commissionType, rates, touched]);

	const parsed = Number(rate);
	const invalid = rate === '' || Number.isNaN(parsed) || parsed < 0 || parsed > 100 || startAt === '';

	// Durées par défaut. Elles sont configurables côté API
	// (PARTNER_COMMISSION_YEARS_APPORTEUR / _PARTNER) : ce texte décrit le cas standard,
	// et la date réellement posée reste lisible et corrigeable sur la fiche partenaire
	// (« Clients attribués » → Termes).
	const endRule =
		commissionType === 'partner'
			? 'Commissionnée 2 ans à compter du début, alignée sur la fin du trimestre civil.'
			: 'Commissionnée 1 an à compter du début, alignée sur la fin du trimestre civil.';

	return (
		<>
			<Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
				Termes de l'affaire pour <strong>{partner.name}</strong>.
			</Typography>

			<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
				<TextField
					select
					size="small"
					label="Type d'affaire"
					value={commissionType}
					onChange={(e) => setCommissionType(e.target.value)}
					sx={{ flex: '1 1 200px' }}
				>
					{DEAL_TYPES.map((t) => (
						<MenuItem key={t.id} value={t.id}>
							{t.name}
						</MenuItem>
					))}
				</TextField>
				<TextField
					size="small"
					type="number"
					label="Taux de commission"
					value={rate}
					onChange={(e) => {
						setTouched(true);
						setRate(e.target.value);
					}}
					error={touched && invalid}
					InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
					sx={{ flex: '0 1 160px' }}
				/>
				<TextField
					size="small"
					type="date"
					label="Début de commissionnement"
					value={startAt}
					onChange={(e) => setStartAt(e.target.value)}
					error={startAt === ''}
					InputLabelProps={{ shrink: true }}
					sx={{ flex: '0 1 200px' }}
				/>
			</Box>

			{/* La fenêtre de commissionnement est FIGÉE à la création et découle du type.
			    L'admin ne la choisit pas — mais il doit la lire avant de valider : c'est ce
			    qui sépare une affaire d'un an d'une affaire de deux ans. */}
			<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
				{endRule}
			</Typography>

			<Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 3 }}>
				<Button onClick={onBack}>Changer de partenaire</Button>
				<Button variant="contained" disabled={invalid} onClick={() => onConfirm(commissionType, parsed, startAt)}>
					Associer
				</Button>
			</Box>
		</>
	);
};
// #endregion

// #region AssociateDialog
export const AssociateDialog: React.FC<Props> = ({ open, onClose, onConfirm }) => {
	const [picked, setPicked] = useState<IPartner | null>(null);
	const rates = useDealRates(open);

	const close = useCallback(() => {
		setPicked(null);
		onClose();
	}, [onClose]);

	return (
		<Dialog open={open} onClose={close} fullWidth maxWidth="sm">
			<DialogTitle>{picked ? "Termes de l'affaire" : 'Associer un partenaire'}</DialogTitle>
			<DialogContent>
				{picked ? (
					<DealTermsStep
						partner={picked}
						rates={rates}
						onBack={() => setPicked(null)}
						onConfirm={(commissionType, rate, startAt) => {
							onConfirm(picked, commissionType, rate, startAt);
							setPicked(null);
						}}
					/>
				) : (
					<PartnerSearchStep onPick={setPicked} />
				)}
			</DialogContent>
		</Dialog>
	);
};
// #endregion
